import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { loadRazorpayScript } from "@/lib/razorpay";
import { Table, inr, when, STATUS } from "./shared";

const SRC = { campaign: "Campaign", test: "Test call", one_off: "Lead AI call (simulated)" };

export function CallHistory() {
  const q = useQuery({ queryKey: ["ob", "calls"], queryFn: async () => (await api.get("/outbound/calls")).data, refetchInterval: 5000 });
  return <Table head={["When", "Source", "Campaign", "Customer", "Phone", "Purpose", "Status", "Outcome", "Duration", "Credits"]} id="ob-history" empty="No outbound calls yet."
    rows={(q.data || []).map((c) => [when(c.at), `${SRC[c.source] || c.source}${c.mocked ? " · MOCKED" : ""}`, c.campaign, c.customer, c.phone, c.purpose,
      STATUS[c.status] || c.status, c.outcome, `${c.duration_s || 0}s`, inr(c.credits_paise)])} />;
}

export function DoNotCall() {
  const [m, setM] = useState(""), qc = useQueryClient();
  const q = useQuery({ queryKey: ["ob", "dnc"], queryFn: async () => (await api.get("/outbound/dnc")).data });
  const add = () => api.post("/outbound/dnc", { mobile: m }).then(() => { setM(""); toast.success("Added to do-not-call"); qc.invalidateQueries({ queryKey: ["ob"] }); }).catch((e) => toast.error(apiErr(e)));
  return (
    <div className="space-y-3" data-testid="ob-dnc">
      <p className="text-xs text-slate-600">Numbers here are never called by any campaign or AI call from your business. Customers who ask Shally to stop are added automatically.</p>
      <div className="flex gap-2"><input value={m} onChange={(e) => setM(e.target.value)} placeholder="10-digit mobile" className="rounded border px-2 py-1 text-sm" data-testid="ob-dnc-input" />
        <Button size="sm" onClick={add} data-testid="ob-dnc-add">Add</Button></div>
      <Table head={["Mobile", "Reason", "Added"]} id="ob-dnc-list" empty="No opted-out numbers." rows={(q.data || []).map((d) => [d.mobile, d.reason, when(d.at)])} />
    </div>
  );
}

function Buy({ pricing }) {
  const qc = useQueryClient(), min = pricing.min_recharge_inr || 1000;
  const [amt, setAmt] = useState(min);
  const gst = Math.round(amt * (pricing.gst_percent_applied || 0)) / 100;
  const buy = async () => {
    try {
      const { data: o } = await api.post("/outbound/credits/order", { amount_inr: Number(amt) });
      if (!(await loadRazorpayScript())) return toast.error("Could not load checkout");
      new window.Razorpay({ key: o.key_id, order_id: o.order_id, amount: o.charge_paise, currency: "INR", name: "Pawkt outbound wallet (TEST)",
        handler: (r) => api.post("/outbound/credits/verify", r).then(() => { toast.success("Test credits added"); qc.invalidateQueries({ queryKey: ["ob"] }); }).catch((e) => toast.error(apiErr(e))) }).open();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="ob-buy">
      <span className="text-sm">Recharge ₹</span>
      <input type="number" min={min} step={100} value={amt} onChange={(e) => setAmt(e.target.value)} className="w-28 rounded border px-2 py-1 text-sm" data-testid="ob-recharge-amount" />
      <span className="text-xs text-slate-600" data-testid="ob-recharge-gst">+ GST ₹{gst.toFixed(2)} (minimum ₹{min.toLocaleString("en-IN")})</span>
      <Button size="sm" variant="outline" disabled={Number(amt) < min} onClick={buy} data-testid="ob-recharge-btn">Add credit (test mode)</Button>
    </div>
  );
}

export function Credits({ pricing }) {
  const q = useQuery({ queryKey: ["ob", "credits"], queryFn: async () => (await api.get("/outbound/credits")).data });
  const w = q.data?.wallet;
  return (
    <div className="space-y-3" data-testid="ob-credits">
      <p className="text-sm">Paid credit <b data-testid="ob-credit-available">{inr(w?.available_paise)}</b> · promotional credit <b data-testid="ob-promo-available">{inr(w?.promo_available_paise)}</b>{w?.promo_expires_at && w?.promo_available_paise > 0 ? ` (expires ${w.promo_expires_at.slice(0, 10)})` : ""} · held {inr((w?.reserved_paise || 0) + (w?.promo_reserved_paise || 0))}</p>
      <p className="text-xs text-slate-600" data-testid="ob-prices">Pay as you use, no monthly fee: calls {inr(pricing.rate_paise_per_min)}/min ({pricing.pulse_seconds}s billing step) · AI campaign generation {inr(pricing.gen_paise)} each, charged only when it succeeds · plus GST {pricing.gst_percent_applied}%{pricing.provisional ? " · PROVISIONAL prices, not yet approved" : ""}</p>
      <p className="text-xs text-slate-500">Outbound credit is separate from your inbound AI Receptionist plan. Promotional credit is used first, expires and is never refundable. Unanswered or failed calls: {inr(pricing.failed_call_paise)}.</p>
      <Buy pricing={pricing} />
      <Table head={["When", "Type", "Amount", "Note"]} id="ob-ledger" empty="No credit activity yet."
        rows={(q.data?.ledger || []).map((l) => [when(l.at), l.kind.replaceAll("_", " "), inr(l.paise), l.note])} />
    </div>
  );
}
